/** Accent presets for app primary color (Appearance settings). */

export type AccentId =
  | 'blue'
  | 'indigo'
  | 'cyan'
  | 'teal'
  | 'emerald'
  | 'violet'
  | 'rose'
  | 'orange'
  | 'amber'
  | 'slate';

export type PrimaryScale = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
};

export interface AccentPreset {
  id: AccentId;
  /** i18n key under settings.accent* */
  labelKey: string;
  /** Hex swatch (500) for the settings UI */
  swatch: string;
  /** Full Tailwind-like scale as hex */
  scale: PrimaryScale;
}

/** Convert #RRGGBB to "R G B" for Tailwind rgb(var(--x) / <alpha-value>). */
export function hexToRgbChannels(hex: string): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `${r} ${g} ${b}`;
}

export const ACCENT_PRESETS: AccentPreset[] = [
  {
    id: 'blue',
    labelKey: 'settings.accentBlue',
    swatch: '#3b82f6',
    scale: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
  },
  {
    id: 'indigo',
    labelKey: 'settings.accentIndigo',
    swatch: '#6366f1',
    scale: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
  },
  {
    id: 'cyan',
    labelKey: 'settings.accentCyan',
    swatch: '#06b6d4',
    scale: {
      50: '#ecfeff',
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',
      600: '#0891b2',
      700: '#0e7490',
      800: '#155e75',
      900: '#164e63',
    },
  },
  {
    id: 'teal',
    labelKey: 'settings.accentTeal',
    swatch: '#14b8a6',
    scale: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
    },
  },
  {
    id: 'emerald',
    labelKey: 'settings.accentEmerald',
    swatch: '#10b981',
    scale: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
  },
  {
    id: 'violet',
    labelKey: 'settings.accentViolet',
    swatch: '#8b5cf6',
    scale: {
      50: '#f5f3ff',
      100: '#ede9fe',
      200: '#ddd6fe',
      300: '#c4b5fd',
      400: '#a78bfa',
      500: '#8b5cf6',
      600: '#7c3aed',
      700: '#6d28d9',
      800: '#5b21b6',
      900: '#4c1d95',
    },
  },
  {
    id: 'rose',
    labelKey: 'settings.accentRose',
    swatch: '#f43f5e',
    scale: {
      50: '#fff1f2',
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',
      800: '#9f1239',
      900: '#881337',
    },
  },
  {
    id: 'orange',
    labelKey: 'settings.accentOrange',
    swatch: '#f97316',
    scale: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
  },
  {
    id: 'amber',
    labelKey: 'settings.accentAmber',
    swatch: '#f59e0b',
    scale: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
    },
  },
  {
    id: 'slate',
    labelKey: 'settings.accentSlate',
    swatch: '#64748b',
    scale: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
  },
];

export const DEFAULT_ACCENT_ID: AccentId = 'blue';

export function getAccentPreset(id: string | null | undefined): AccentPreset {
  return ACCENT_PRESETS.find((p) => p.id === id) ?? ACCENT_PRESETS[0];
}

const PRIMARY_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/** Apply primary CSS variables on documentElement (RGB channels for Tailwind alpha). */
export function applyAccentToDocument(preset: AccentPreset): void {
  const root = document.documentElement;
  for (const step of PRIMARY_STEPS) {
    root.style.setProperty(`--primary-${step}`, hexToRgbChannels(preset.scale[step]));
  }
  root.style.setProperty('--color-primary', preset.scale[500]);
  root.style.setProperty('--color-primary-hover', preset.scale[600]);
  root.style.setProperty('--color-primary-light', preset.scale[100]);
  root.style.setProperty('--color-accent', preset.scale[400]);
  root.dataset.accent = preset.id;
}
